"use client"

/**
 * Article FAQ Section
 *
 * Renders 3-5 FAQ pairs with answers extracted from article content.
 * Answers render verbatim — no injected self-citation (FIX-18 style rule:
 * a site cannot cite itself as an authority).
 * CTA links point to verified money pages (hub pages, collections) via MoneyPageLink.
 * Tool CTAs use plain links (tools are NOT money pages).
 *
 * Collapsible accordion pattern — first FAQ open by default.
 */

import { useState } from 'react'
import Link from 'next/link'
import { MoneyPageLink } from '@/components/ui/money-page-link'
import type { GeneratedFAQ } from '@/lib/article-faq-generator'
import { PartLinkedText } from '@/components/filters/part-linked-text'

interface ArticleFAQSectionProps {
  faqs: GeneratedFAQ[]
  articleTitle: string
}

export function ArticleFAQSection({ faqs, articleTitle }: ArticleFAQSectionProps) {
  const [openIndex, setOpenIndex] = useState(0)

  if (!faqs || faqs.length === 0) return null

  return (
    <div className="mt-6 pt-3 border-t border-gray-200">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-1.5">
        <svg className="w-6 h-6 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Frequently Asked Questions
      </h2>

      <div className="space-y-3">
        {faqs.map((faq, index) => (
          <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Question (clickable) */}
            <button
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
              aria-expanded={openIndex === index}
            >
              <span className="text-sm sm:text-base font-semibold text-gray-900 pr-4">
                {faq.question}
              </span>
              <svg
                className={`w-5 h-5 text-gray-500 shrink-0 transition-transform duration-200 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Answer (collapsible) */}
            {openIndex === index && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <p className="text-sm text-gray-700 leading-relaxed mt-3">
                  <PartLinkedText text={faq.answer} segments={faq.answerSegments} />
                </p>

                {/* CTA link to money page */}
                {faq.ctaHref && faq.ctaText && (
                  <div className="mt-3">
                    {faq.ctaType === 'hub' || faq.ctaType === 'collection' ? (
                      <MoneyPageLink
                        href={faq.ctaHref}
                        type={faq.ctaType}
                        title={faq.ctaText.replace(' at Fridge.com', '')}
                      >
                        {faq.ctaText.replace(' at Fridge.com', '')}
                      </MoneyPageLink>
                    ) : (
                      <Link
                        href={faq.ctaHref}
                        className="text-sm text-blue-600 hover:text-blue-800 underline transition-colors"
                      >
                        {faq.ctaText.replace(' at Fridge.com', '')}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
